import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Plus, Edit2, Trash2, Lock, Unlock, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  description: "",
  system_prompt: "",
  category: "",
  model: "gpt-4o",
  status: "unlocked",
  access_type: "free",
  icon: "Sparkles",
};

export default function AdminTools() {
  const [tools, setTools] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/admin/tools").then((r) => setTools(r.data));

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name,
      description: t.description,
      system_prompt: t.system_prompt,
      category: t.category,
      model: t.model,
      status: t.status,
      access_type: t.access_type,
      icon: t.icon || "Sparkles",
      conversation_starters: t.conversation_starters || [],
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/admin/tools/${editing.id}`, form);
        toast.success("Tool updated");
      } else {
        await api.post("/admin/tools", form);
        toast.success("Tool created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/tools/${t.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleLock = async (t) => {
    const next = t.status === "locked" ? "unlocked" : "locked";
    try {
      await api.put(`/admin/tools/${t.id}`, { status: next });
      load();
    } catch {
      toast.error("Failed to toggle");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">
          {tools.length} agents
        </div>
        <button
          data-testid="admin-create-tool-btn"
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-4 py-2.5 glow-primary"
        >
          <Plus className="w-4 h-4" /> New Agent
        </button>
      </div>

      <div className="border border-zinc-900 rounded-2xl overflow-hidden bg-zinc-950/40">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Category</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Model</th>
              <th className="text-left px-4 py-3">Access</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tools.map((t) => (
              <tr
                key={t.id}
                data-testid={`admin-tool-row-${t.id}`}
                className="border-t border-zinc-900 hover:bg-zinc-900/30"
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-white">{t.name}</div>
                  <div className="text-xs text-zinc-500 line-clamp-1 max-w-md">
                    {t.description}
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-400 hidden md:table-cell">
                  {t.category}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  <span className="font-mono text-xs text-zinc-500">{t.model}</span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${
                      t.access_type === "paid"
                        ? "text-amber-300 border-amber-500/30 bg-amber-500/5"
                        : t.access_type === "private"
                        ? "text-rose-300 border-rose-500/30 bg-rose-500/5"
                        : "text-emerald-300 border-emerald-500/30 bg-emerald-500/5"
                    }`}
                  >
                    {t.access_type}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`admin-toggle-lock-${t.id}`}
                    onClick={() => toggleLock(t)}
                    className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-md border transition-colors ${
                      t.status === "locked"
                        ? "text-amber-300 border-amber-500/30 hover:bg-amber-500/10"
                        : "text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/10"
                    }`}
                  >
                    {t.status === "locked" ? (
                      <Lock className="w-3 h-3" />
                    ) : (
                      <Unlock className="w-3 h-3" />
                    )}
                    {t.status}
                  </button>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    data-testid={`admin-edit-tool-${t.id}`}
                    onClick={() => openEdit(t)}
                    className="text-zinc-400 hover:text-emerald-400 p-1.5"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`admin-delete-tool-${t.id}`}
                    onClick={() => remove(t)}
                    className="text-zinc-400 hover:text-rose-400 p-1.5"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {tools.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-zinc-500">
                  No agents yet. Create your first one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <Modal onClose={() => setOpen(false)} title={editing ? "Edit Agent" : "New Agent"}>
          <form onSubmit={submit} className="space-y-4">
            <Field label="Name">
              <input
                data-testid="admin-form-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-dark"
              />
            </Field>
            <Field label="Short description">
              <input
                data-testid="admin-form-description"
                required
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                className="input-dark"
              />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Category">
                <input
                  data-testid="admin-form-category"
                  required
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="input-dark"
                />
              </Field>
              <Field label="Model">
                <select
                  data-testid="admin-form-model"
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                  className="input-dark"
                >
                  <option value="gpt-4o">gpt-4o</option>
                  <option value="gpt-4o-mini">gpt-4o-mini</option>
                  <option value="gpt-4.1">gpt-4.1</option>
                  <option value="gpt-4.1-mini">gpt-4.1-mini</option>
                  <option value="gpt-5">gpt-5</option>
                  <option value="gpt-5-mini">gpt-5-mini</option>
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Access type">
                <select
                  data-testid="admin-form-access"
                  value={form.access_type}
                  onChange={(e) =>
                    setForm({ ...form, access_type: e.target.value })
                  }
                  className="input-dark"
                >
                  <option value="free">free</option>
                  <option value="paid">paid</option>
                  <option value="private">private</option>
                </select>
              </Field>
              <Field label="Status">
                <select
                  data-testid="admin-form-status"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="input-dark"
                >
                  <option value="unlocked">unlocked</option>
                  <option value="locked">locked</option>
                </select>
              </Field>
            </div>
            <Field label="Icon (lucide)">
              <select
                data-testid="admin-form-icon"
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                className="input-dark"
              >
                {[
                  "Sparkles",
                  "Briefcase",
                  "Code2",
                  "PenLine",
                  "Megaphone",
                  "Mail",
                  "Lightbulb",
                  "Brain",
                  "BarChart3",
                ].map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="System prompt">
              <textarea
                data-testid="admin-form-prompt"
                required
                rows={10}
                value={form.system_prompt}
                onChange={(e) =>
                  setForm({ ...form, system_prompt: e.target.value })
                }
                className="input-dark font-mono text-xs leading-relaxed"
              />
            </Field>
            <Field label="Conversation starters (one per line, max 6)">
              <textarea
                data-testid="admin-form-starters"
                rows={4}
                value={(form.conversation_starters || []).join("\n")}
                onChange={(e) =>
                  setForm({
                    ...form,
                    conversation_starters: e.target.value
                      .split("\n")
                      .map((s) => s.trim())
                      .filter(Boolean)
                      .slice(0, 6),
                  })
                }
                placeholder={"Help me design an entry offer\nDraft a 7-day launch plan"}
                className="input-dark text-sm leading-relaxed"
              />
              <div className="text-[10px] text-zinc-600 mt-1">
                Shown as clickable chips on the empty chat screen.
              </div>
            </Field>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm text-zinc-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                data-testid="admin-form-submit"
                type="submit"
                disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-5 py-2.5 glow-primary disabled:opacity-60"
              >
                {busy ? "Saving..." : editing ? "Save Changes" : "Create Agent"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      <style>{`
        .input-dark {
          width: 100%;
          background: rgba(24,24,27,0.8);
          border: 1px solid #27272a;
          border-radius: 0.625rem;
          padding: 0.65rem 0.85rem;
          color: #fafafa;
          outline: none;
          transition: border-color 200ms;
          font-size: 14px;
        }
        .input-dark:focus { border-color: rgba(5,150,105,0.5); }
      `}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">
      {label}
    </div>
    {children}
  </label>
);

const Modal = ({ children, onClose, title }) => (
  <div
    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
    onClick={onClose}
  >
    <div
      className="relative bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-2xl my-8 p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-2xl tracking-tighter font-black">
          {title}
        </h3>
        <button
          onClick={onClose}
          className="text-zinc-500 hover:text-white"
          data-testid="admin-modal-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      {children}
    </div>
  </div>
);
